import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  Speed as SprintIcon,
  Person as AssigneeIcon,
  Flag as StatusIcon,
  FormatListNumbered as LineupIcon,
  AccountTree as DivisionIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { useWorkspace } from "../../utils/WorkspaceContext";
import { useAssignees } from "../../utils/AssigneeContext";
import { getStatusMeta } from "../../utils/StatusChip";
import { slotOptions } from "../../utils/sprintSlots";

// The 3 writable sprint slots (Current / Next / Bucket), labelled without dates.
const SPRINT_SLOT_OPTIONS = slotOptions();

/**
 * Floating bulk-action bar shown when one or more tasks are selected in the
 * List view. Each action opens a small picker and calls `onApply` with an
 * object of API field → value (e.g. `{ status: "Doing" }`). Department/Division
 * is applied together: picking a Division also sets its parent Department.
 *
 * Sprint is set via the 4-slot abstraction (Current / Next / Bucket). The
 * backend resolves the slot to each task's own track sprint, so the action
 * works across a multi-track selection.
 *
 * @param {Object} props
 * @param {number} props.selectedCount
 * @param {function} props.onApply - (updates) => void
 * @param {function} props.onClear
 * @param {string[]} props.statusOptions
 */
const BulkEditBar = ({
  selectedCount,
  onApply,
  onClear,
  statusOptions,
}) => {
  const { currentWorkspace } = useWorkspace();
  const { assignees } = useAssignees();

  // Each action owns an anchor; only one picker is open at a time.
  const [anchor, setAnchor] = useState({ el: null, kind: null });
  const openPicker = (kind) => (e) => setAnchor({ el: e.currentTarget, kind });
  const closePicker = () => setAnchor({ el: null, kind: null });

  const [lineupValue, setLineupValue] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Lazy-load divisions + departments when that picker opens.
  useEffect(() => {
    if (anchor.kind !== "division" || !currentWorkspace?.id) return;
    let cancelled = false;
    const params = { params: { workspace_id: currentWorkspace.id } };
    Promise.all([api.get("divisions/", params), api.get("departments/", params)])
      .then(([divRes, deptRes]) => {
        if (cancelled) return;
        setDivisions(Array.isArray(divRes.data) ? divRes.data : []);
        setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
      })
      .catch((err) => console.error("Error fetching divisions:", err));
    return () => {
      cancelled = true;
    };
  }, [anchor.kind, currentWorkspace?.id]);

  const apply = (updates) => {
    onApply(updates);
    closePicker();
  };

  const initials = (name) =>
    (name || "?")
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const displayName = (a) => a?.full_name || a?.username || "";

  const ActionButton = ({ icon, label, kind, disabled, disabledHint }) => {
    const btn = (
      <span>
        <Button
          size="small"
          startIcon={icon}
          onClick={openPicker(kind)}
          disabled={disabled}
          sx={{ color: "inherit", whiteSpace: "nowrap" }}
        >
          {label}
        </Button>
      </span>
    );
    return disabled && disabledHint ? (
      <Tooltip title={disabledHint}>{btn}</Tooltip>
    ) : (
      btn
    );
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: (theme) => theme.zIndex.drawer + 2,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 3,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          maxWidth: "calc(100vw - 48px)",
          overflowX: "auto",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, mr: 1, whiteSpace: "nowrap" }}>
          {selectedCount} selected
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.3)" }} />
        <ActionButton icon={<SprintIcon />} label="Sprint" kind="sprint" />
        <ActionButton icon={<AssigneeIcon />} label="Assignee" kind="assignee" />
        <ActionButton icon={<StatusIcon />} label="Status" kind="status" />
        <ActionButton icon={<LineupIcon />} label="Line up" kind="lineup" />
        <ActionButton icon={<DivisionIcon />} label="Dept / Division" kind="division" />
        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.3)" }} />
        <Tooltip title="Clear selection">
          <IconButton size="small" onClick={onClear} sx={{ color: "inherit" }}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Sprint picker — the 3 writable slots. The backend maps each to the
          selected task's own track sprint (works across multiple tracks). */}
      <Menu
        anchorEl={anchor.el}
        open={anchor.kind === "sprint"}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {SPRINT_SLOT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => apply({ sprint_slot: opt.value })}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Assignee picker */}
      <Menu
        anchorEl={anchor.el}
        open={anchor.kind === "assignee"}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MenuItem onClick={() => apply({ assignee: null })}>
          <em>Unassigned</em>
        </MenuItem>
        <Divider />
        {assignees.map((a) => (
          <MenuItem key={a.id} onClick={() => apply({ assignee: a.id })} sx={{ gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: "0.7rem" }}>
              {initials(displayName(a))}
            </Avatar>
            {displayName(a)}
          </MenuItem>
        ))}
      </Menu>

      {/* Status picker */}
      <Menu
        anchorEl={anchor.el}
        open={anchor.kind === "status"}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {statusOptions.map((status) => {
          const meta = getStatusMeta(status);
          return (
            <MenuItem key={status} onClick={() => apply({ status })} sx={{ gap: 1 }}>
              <Box
                component="span"
                sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: meta.solid, flexShrink: 0 }}
              />
              {meta.short}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Line up picker */}
      <Popover
        anchorEl={anchor.el}
        open={anchor.kind === "lineup"}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (lineupValue !== "") apply({ line_up: Number(lineupValue) });
          }}
          sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <TextField
            autoFocus
            size="small"
            type="number"
            label="Line up"
            value={lineupValue}
            onChange={(e) => setLineupValue(e.target.value)}
            sx={{ width: 110 }}
          />
          <Button type="submit" variant="contained" disabled={lineupValue === ""}>
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Department / Division picker */}
      <Popover
        anchorEl={anchor.el}
        open={anchor.kind === "division"}
        onClose={closePicker}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, width: 260 }}>
          <TextField
            select
            size="small"
            label="Division (sets its Department)"
            defaultValue=""
            onChange={(e) => {
              const div = divisions.find((d) => d.id === e.target.value);
              if (div) apply({ division: div.id, department: div.department ?? null });
            }}
          >
            {divisions.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
                {d.department_name ? ` — ${d.department_name}` : ""}
              </MenuItem>
            ))}
          </TextField>
          <Divider>or</Divider>
          <TextField
            select
            size="small"
            label="Department only"
            defaultValue=""
            onChange={(e) => apply({ department: e.target.value })}
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Popover>
    </>
  );
};

export default BulkEditBar;
